<template>
  <div>
    <div class="mt-5 d-flex align-center">
      <h4
        v-if="objectType === 'easy-event' || objectType === 'detail-event'"
        class="text-h6"
      >
        Koordinaten
      </h4>
      <h3 v-else>Koordinaten</h3>
      <v-tooltip location="bottom">
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props" size="12" class="mb-3">
            mdi-information-outline
          </v-icon>
        </template>
        <span v-if="objectType === 'bookable'"
          >Diese Koordinaten werden verwendet, um das Bookable auf der Karte
          anzuzeigen.</span
        >
        <span v-else
          >Diese Koordinaten werden verwendet, um das Event auf der Karte
          anzuzeigen.
        </span>
      </v-tooltip>
    </div>
    <v-divider v-if="objectType === 'easy-event'"></v-divider>
    <v-row class="mt-2" dense>
      <v-col>
        <v-text-field
          bg-color="accent"
          variant="filled"
          label="Latitude"
          v-model="localLat"
          :rules="[isNumber]"
          full-width
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          bg-color="accent"
          variant="filled"
          label="Longitude"
          v-model="localLng"
          :rules="[isNumber]"
          full-width
        ></v-text-field>
      </v-col>
      <v-col
        :cols="objectType === 'bookable' ? 2 : 3"
        class="d-none d-md-flex mt-2"
      >
        <v-btn variant="text" color="primary" @click="resetMarker"
          >Zurücksetzen</v-btn
        >
        <v-btn variant="text" color="error" @click="clearCoordinates"
          >Klar</v-btn
        >
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="2"
        class="d-flex d-md-none"
        style="margin-top: -16px; margin-bottom: 8px"
      >
        <v-btn variant="text" color="primary" @click="resetMarker"
          >Zurücksetzen</v-btn
        >
        <v-btn variant="text" color="error" @click="clearCoordinates"
          >Klar</v-btn
        >
      </v-col>
    </v-row>
    <div id="map-container" style="position: relative; height: 300px">
      <div id="map" style="height: 100%; z-index: 0"></div>
      <div
        class="center-marker"
        style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -100%);
          pointer-events: none;
        "
      >
        <v-icon size="28" color="#0099DB"> mdi-map-marker </v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  name: "LocationPicker",
  props: {
    latitude: { type: Number, default: null },
    longitude: { type: Number, default: null },
    objectType: { type: String, required: true },
  },
  emits: ["update:latitude", "update:longitude"],
  data() {
    return {
      map: null,
      localLat: this.latitude,
      localLng: this.longitude,
      defaultCenter: [
        Number(process.env.VUE_APP_MAP_CENTER_LATITUDE || 49.4678),
        Number(process.env.VUE_APP_MAP_CENTER_LONGITUDE || 7.1691),
      ],
      initialLat: this.latitude,
      initialLng: this.longitude,
      zoom: 15,
      isNumber: (v) =>
        !v ||
        /^-?\d+(\.\d+)?$/.test(v) ||
        "Nur Zahlen mit Dezimalpunkt erlaubt",
    };
  },
  watch: {
    localLat(val) {
      this.$emit("update:latitude", val === null ? null : parseFloat(val));
    },
    localLng(val) {
      this.$emit("update:longitude", val === null ? null : parseFloat(val));
    },
    "$vuetify.theme.global.name"() {
      if (this.map) {
        this.map.off();
        this.map.remove();
        this.map = null;

        this.$nextTick(() => {
          this.initMap();
        });
      }
    },
  },
  mounted() {
    this.initMap();
  },
  methods: {
    initMap() {
      const center =
        this.localLat && this.localLng
          ? [this.localLat, this.localLng]
          : this.defaultCenter;

      // basemap.de only covers Germany, so keep map around it
      this.map = L.map("map", {
        zoomControl: true,
        minZoom: 6,
        maxBounds: [
          [46.5, 5.0],
          [55.5, 16.0],
        ],
        maxBoundsViscosity: 1.0,
      }).setView(center, this.zoom);

      const isDark = this.$vuetify.theme.global.name === "dark";
      const tileUrl = isDark
        ? "https://sgx.geodatenzentrum.de/wmts_basemapde/tile/1.0.0/de_basemapde_web_raster_grau/default/GLOBAL_WEBMERCATOR/{z}/{y}/{x}.png"
        : "https://sgx.geodatenzentrum.de/wmts_basemapde/tile/1.0.0/de_basemapde_web_raster_farbe/default/GLOBAL_WEBMERCATOR/{z}/{y}/{x}.png";

      this.map.attributionControl.setPrefix(false);
      const tileLayer = L.tileLayer(tileUrl, {
        maxZoom: 19,
        attribution: `&copy; GeoBasis-DE / <a href='https://www.bkg.bund.de'>BKG</a> (${new Date().getFullYear()}) <a href='https://creativecommons.org/licenses/by/4.0/'>CC BY 4.0</a>`,
      }).addTo(this.map);

      // basemap.de has no dark style, so invert the tiles in dark mode
      if (isDark) {
        tileLayer.getContainer().style.filter =
          "invert(1) hue-rotate(180deg) brightness(0.9) contrast(0.9)";
      }

      this.map.on("moveend", () => {
        const center = this.map.getCenter();
        this.localLat = parseFloat(center.lat.toFixed(6));
        this.localLng = parseFloat(center.lng.toFixed(6));
      });
    },
    resetMarker() {
      const lat = this.initialLat ?? this.defaultCenter[0];
      const lng = this.initialLng ?? this.defaultCenter[1];

      this.map.setView([lat, lng], 13);
      this.localLat = lat;
      this.localLng = lng;
    },
    clearCoordinates() {
      this.localLat = null;
      this.localLng = null;
    },
  },
};
</script>
